import { BrowserPushSettings } from "../../components/admin/BrowserPushSettings";
import { removeBrowserPush } from "../../lib/push";
import {
  BarChart3,
  CalendarClock,
  CircleGauge,
  CloudOff,
  LogOut,
  RefreshCw,
  Settings,
} from "lucide-react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { tenantTheme } from "../../lib/branding";
import { BrandMark } from "../../components/BrandMark";
import { ApiError, apiFetch } from "../../lib/api";
import { flushQueue, listQueuedMutations } from "../../lib/offline-queue";
import { authClient } from "../../lib/auth-client";
import type { BootstrapData } from "./types";

interface AdminContextValue {
  data: BootstrapData;
  refresh: () => Promise<void>;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export const useAdmin = () => {
  const value = useContext(AdminContext);
  if (!value) throw new Error("useAdmin must be used inside AdminShell");
  return value;
};

const nav = [
  { to: "/admin", end: true, label: "Live-Desk", icon: CircleGauge },
  { to: "/admin/events", label: "Events", icon: CalendarClock },
  { to: "/admin/stats", label: "Statistik", icon: BarChart3 },
  { to: "/admin/settings", label: "Einstellungen", icon: Settings },
];

export function AdminShell() {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      setData(await apiFetch<BootstrapData>("/api/admin/bootstrap"));
      setLoadError(false);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setLoadError(true);
    }
  };

  const refreshQueue = () =>
    listQueuedMutations().then((items) => setQueueCount(items.length));

  useEffect(() => {
    void refresh();
    void refreshQueue();
    const becameOnline = () => {
      setOnline(true);
      void flushQueue().then(() => {
        void refreshQueue();
        void refresh();
      });
    };
    const becameOffline = () => setOnline(false);
    const queueChanged = () => void refreshQueue();
    window.addEventListener("online", becameOnline);
    window.addEventListener("offline", becameOffline);
    window.addEventListener("vzlive:queue-change", queueChanged);
    return () => {
      window.removeEventListener("online", becameOnline);
      window.removeEventListener("offline", becameOffline);
      window.removeEventListener("vzlive:queue-change", queueChanged);
    };
  }, []);

  if (!data) {
    if (loadError) {
      return (
        <div className="admin-loader admin-loader--error">
          <BrandMark />
          <span>
            {online ? "Server nicht erreichbar" : "Offline – keine Verbindung"}
          </span>
          <div className="admin-loader-actions">
            <button
              className="admin-loader-retry"
              onClick={() => void refresh()}
            >
              <RefreshCw size={16} aria-hidden="true" />
              Erneut versuchen
            </button>
            <a href="/">Zur Startseite</a>
          </div>
        </div>
      );
    }
    return (
      <div className="admin-loader">
        <BrandMark />
        <span>Live-Desk wird geöffnet</span>
      </div>
    );
  }

  const signOut = async () => {
    await removeBrowserPush().catch(() => undefined);
    await authClient.signOut();
    navigate("/admin/login", { replace: true });
  };

  return (
    <AdminContext.Provider value={{ data, refresh }}>
      <div
        className="admin-shell"
        style={tenantTheme(
          data.tenant.primaryColor,
          data.tenant.secondaryColor,
        )}
      >
        <header className="forum-desk-header">
          <a href="/" aria-label="Öffentliche Startseite">
            <BrandMark />
          </a>
          <div className="forum-desk-tenant">
            {data.tenant.logoUrl && <img src={data.tenant.logoUrl} alt="" />}
            <strong>{data.tenant.name}</strong>
          </div>
          <div className="forum-desk-user">
            <span>{data.user.name}</span>
            {data.role !== "reporter" && (
              <BrowserPushSettings
                key={`${data.tenant.id}:${data.user.id}`}
                scope={`${data.tenant.id}:${data.user.id}`}
              />
            )}
            <button onClick={() => void signOut()} aria-label="Abmelden">
              <LogOut size={20} aria-hidden="true" />
            </button>
          </div>
        </header>
        <nav className="forum-desk-nav" aria-label="Redaktion">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}>
              <Icon size={18} aria-hidden="true" />
              <span>{label}</span>
              {label === "Live-Desk" && data.counts.pendingSubmissions > 0 && (
                <b aria-label={`${data.counts.pendingSubmissions} offen`}>
                  {data.counts.pendingSubmissions}
                </b>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="admin-workspace">
          {(!online || queueCount > 0) && (
            <div
              className={`admin-connectivity ${online ? "admin-connectivity--sync" : ""}`}
            >
              {online ? <RefreshCw size={16} /> : <CloudOff size={16} />}
              <span>
                {online
                  ? `${queueCount} ${queueCount === 1 ? "Beitrag wird" : "Beiträge werden"} synchronisiert`
                  : "Offline – neue Beiträge werden lokal vorgemerkt"}
              </span>
              {online && (
                <button onClick={() => void flushQueue()}>Jetzt senden</button>
              )}
            </div>
          )}
          <main id="main" className="admin-main">
            <Outlet />
          </main>
        </div>
      </div>
    </AdminContext.Provider>
  );
}
